import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupsIcon from "@mui/icons-material/Groups";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import StarIcon from "@mui/icons-material/Star";
import RecyclingIcon from "@mui/icons-material/Recycling";
import SecurityIcon from "@mui/icons-material/Security";

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  return (
    <>
      <SEO 
        title="About Us - Blossom Cleaning Services | Trusted Cleaning Experts in Kampala"
        description="Learn about Blossom Cleaning Services - Kampala's leading cleaning company. 10+ years of experience, 500+ satisfied clients, certified professionals. Eco-friendly cleaning solutions with insurance coverage."
        keywords="about blossom cleaning, cleaning company Kampala, certified cleaners Uganda, professional cleaning team, eco-friendly cleaning Kampala, trusted cleaning services"
        url="https://blossomcleaningservices.com/about"
      />
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated Background Elements */}
        <motion.div
          style={{
            position: "absolute",
            top: "10%",
            left: "5%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
          animate={floatingAnimation}
        />
        <motion.div
          style={{
            position: "absolute",
            bottom: "20%",
            right: "8%",
            width: 80,
            height: 80,
            borderRadius: "30%",
            background: "rgba(255,255,255,0.08)",
            rotate: 45,
          }}
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 2 },
          }}
        />

        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              animate={{
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  color: "white",
                  fontSize: { xs: "2.75rem", md: "4rem" },
                  fontWeight: 800,
                  background:
                    "linear-gradient(45deg, #FFFFFF 30%, #E3F2FD 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                About Blossom City Cleaning
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Typography
                variant="h5"
                sx={{
                  maxWidth: 800,
                  mx: "auto",
                  color: "rgba(255,255,255,0.95)",
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Your trusted partner for professional cleaning services in
                Kampala since 2015
              </Typography>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box
        sx={{ py: { xs: 6, md: 10 }, position: "relative", overflow: "hidden" }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.05) 0%, transparent 50%)`,
          }}
        />

        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    mb: 3,
                    fontWeight: 800,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    fontSize: { xs: "2.5rem", md: "3rem" },
                  }}
                >
                  Our Story
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                    color: "text.secondary",
                  }}
                >
                  Blossom Cleaning Services was founded with a simple mission:
                  to provide exceptional cleaning services that transform spaces
                  and exceed expectations. What started as a small team has
                  grown into Kampala's most trusted cleaning service provider.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                    color: "text.secondary",
                  }}
                >
                  We believe that a clean environment is essential for health,
                  productivity, and well-being. That's why we've dedicated
                  ourselves to delivering meticulous cleaning services with a
                  personal touch, using eco-friendly products and the latest
                  cleaning techniques.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    lineHeight: 1.8,
                    fontSize: "1.1rem",
                    color: "text.secondary",
                  }}
                >
                  Today, we're proud to serve hundreds of satisfied clients
                  across Kampala, from homeowners to businesses, providing a
                  comprehensive range of cleaning and facility management
                  services.
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8 }}
                whileHover={{ scale: 1.02 }}
              >
                <Box
                  component="img"
                  src="/images/cert.JPG"
                  alt="Our team"
                  sx={{
                    width: "100%",
                    borderRadius: 4,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: "background.default",
          position: "relative",
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 2,
                fontWeight: 800,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                fontSize: { xs: "2.5rem", md: "3rem" },
              }}
            >
              Our Core Values
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{
                mb: 8,
                maxWidth: 700,
                mx: "auto",
                fontSize: { xs: "1.1rem", md: "1.25rem" },
                lineHeight: 1.6,
              }}
            >
              The principles that guide everything we do and set us apart in the
              cleaning industry
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {[
              {
                icon: <VerifiedIcon />,
                title: "Trust & Integrity",
                description:
                  "All our staff are thoroughly vetted, background-checked, and insured. We take security and trust seriously.",
                color: "#667eea",
                animation: "pulse",
              },
              {
                icon: <WorkspacePremiumIcon />,
                title: "Excellence",
                description:
                  "We maintain the highest standards in every service we provide, with continuous training and quality checks.",
                color: "#764ba2",
                animation: "bounce",
              },
              {
                icon: <GroupsIcon />,
                title: "Customer First",
                description:
                  "Your satisfaction is our priority. We listen, adapt, and always go the extra mile to exceed expectations.",
                color: "#FF6B35",
                animation: "float",
              },
              {
                icon: <RecyclingIcon />,
                title: "Eco-Friendly",
                description:
                  "We use environmentally safe, non-toxic cleaning products that are safe for your family and pets.",
                color: "#4CAF50",
                animation: "spin",
              },
              {
                icon: <SecurityIcon />,
                title: "Reliability",
                description:
                  "Count on us to be there when you need us, with consistent quality and dependable service every time.",
                color: "#2196F3",
                animation: "pulse",
              },
              {
                icon: <StarIcon />,
                title: "Innovation",
                description:
                  "We continuously adopt new technologies and methods to deliver better, more efficient cleaning solutions.",
                color: "#FFD700",
                animation: "bounce",
              },
            ].map((value, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{
                    y: -10,
                    transition: { duration: 0.3 },
                  }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      p: 4,
                      borderRadius: 4,
                      background:
                        "linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)",
                      border: "1px solid rgba(0,0,0,0.05)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${value.color} 0%, ${value.color}99 100%)`,
                      },
                      "&:hover": {
                        boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
                      },
                      transition: "all 0.4s ease",
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <motion.div
                        animate={
                          value.animation === "pulse"
                            ? {
                                scale: [1, 1.1, 1],
                                transition: { duration: 2, repeat: Infinity },
                              }
                            : value.animation === "bounce"
                            ? {
                                y: [0, -10, 0],
                                transition: { duration: 2, repeat: Infinity },
                              }
                            : value.animation === "float"
                            ? floatingAnimation
                            : value.animation === "spin"
                            ? {
                                rotate: [0, 360],
                                transition: {
                                  duration: 8,
                                  repeat: Infinity,
                                  ease: "linear",
                                },
                              }
                            : {}
                        }
                        style={{ marginBottom: "24px" }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${value.color} 0%, ${value.color}99 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            color: "white",
                          }}
                        >
                          {value.icon}
                        </Box>
                      </motion.div>
                      <Typography
                        variant="h5"
                        sx={{
                          mb: 2,
                          fontWeight: 700,
                          color: "text.primary",
                        }}
                      >
                        {value.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                          fontSize: "1rem",
                        }}
                      >
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 800,
                color: "white",
                fontSize: { xs: "2.5rem", md: "3rem" },
              }}
            >
              By The Numbers
            </Typography>
          </motion.div>

          <Grid container spacing={4} justifyContent="center">
            {[
              { number: "50+", label: "Trained Professionals", delay: 0 },
              { number: "8+", label: "Years of Experience", delay: 0.1 },
              { number: "500+", label: "Satisfied Clients", delay: 0.2 },
              { number: "100%", label: "Satisfaction Guarantee", delay: 0.3 },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: stat.delay }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 4,
                      background: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(10px)",
                      borderRadius: 4,
                      border: "1px solid rgba(255,255,255,0.2)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: "rgba(255,255,255,0.15)",
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.6,
                        delay: stat.delay + 0.2,
                        type: "spring",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          color: "white",
                          fontWeight: 800,
                          mb: 1,
                          fontSize: { xs: "2.5rem", md: "3rem" },
                        }}
                      >
                        {stat.number}
                      </Typography>
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 600,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Footer />
    </>
  );
};

export default About;
